/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import useOpen from '@/hooks/useOpen';
import { useGetUserInfoQuery } from '@/store/apis';
import { Avatar, Bar, Button, MessageBox, Modals, ShellBar, ShellBarItem, Text } from '@ui5/webcomponents-react';
import { useNavigate } from 'react-router-dom';
import HelpComponent from '../components/HelpComponent';
import { useTranslations } from '../hooks/useTranslations';
import { JouleUtils } from '../utils/JouleUtil';
import * as storageUtils from '../utils/storageUtils';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { loadingActions } from '@/store/loadingSlice';
import useVideoDialog from '@/hooks/useVideoDialog';
import axiosInstance from '@/utils/axiosInstance';
import ShellPopover from './ShellPopOver/ShellPopOver';
import { triggerUserInitiatedFeedback } from '@/store/feedbackSlice';

const UI5ShellBar: React.FC = () => {
    const { data: userInfo } = useGetUserInfoQuery(undefined, {
        skip: !storageUtils.getItem('token'),
    });

    const [isOpen, toggle] = useOpen();
    const [isWarningOpen, warningToggle] = useOpen();

    const { t } = useTranslations();
    const navigate = useNavigate();

    const jouleEnabled = useAppSelector(state => state.productList.isJouleEnabled);

    const dispatch = useAppDispatch();
    const selector = useAppSelector(state => state.feedbackReducer);

    const launchJouleClient = () => {
        JouleUtils.showDigitalAssistant();
    };

    const getInitial = (name: string): string => {
        const words = name.trim().split(' ');
        let initial = '';
        for (const word of words) {
            if (word) initial += word[0].toUpperCase();
        }

        return initial;
    };

    const handleLogout = async () => {
        dispatch(loadingActions.startLoading());
        try {
            const currentUrl = window.location.pathname;
            localStorage.setItem('hitUrl', JSON.stringify(currentUrl));
            await axiosInstance.get(`/logout`);
            storageUtils.removeItem(['refreshToken', 'token', 'user', 'code', 'state']);
            window.location.reload();
        } catch (error) {
            console.error('Error logging out:', error);
        } finally {
            dispatch(loadingActions.stopLoading());
        }
    };

    const handleHelp = () => {
        const { close } = Modals.showDialog({
            style: { width: '50%' },
            children: <HelpComponent />,
            footer: (
                <Bar
                    endContent={
                        <Button
                            data-testid="close-help-button"
                            onClick={() => {
                                close();
                            }}
                        >
                            {t('CLOSE_BUTTON')}
                        </Button>
                    }
                />
            ),
        });
    };

    const handleIntroDialog = useVideoDialog({
        title: t('WELCOME_TO_DEPLOYMENT_HUB'),
        src: process.env.REACT_APP_HELP_VIDEO_URL as string,
    });

    return (
        <>
            <ShellBar
                style={{ padding: '0 42px' }}
                data-testid="shell-bar"
                logo={<img alt="SAP Logo" src="/images/sap_logo.svg" data-testid="logo" />}
                notificationsCount="10"
                onProfileClick={() => toggle()}
                onLogoClick={() => {
                    if (userInfo) {
                        navigate('/');
                    } else {
                        navigate('/login');
                    }
                }}
                primaryTitle={t('PLATFORM_TITLE')}
                profile={
                    userInfo ? (
                        <Avatar id="openPopoverBtn" data-testid="avatar" initials={getInitial(`${userInfo.given_name} ${userInfo.family_name}`)} />
                    ) : null
                }
            >
                <ShellBarItem icon="e-learning" text="intro" data-testid="intro-icon" onClick={handleIntroDialog} />
                {userInfo && (
                    <ShellBarItem
                        icon="feedback"
                        text="feedback"
                        data-testid="fb-icon"
                        onClick={async () => {
                            if (userInfo && selector.pxApiInitialized) {
                                await dispatch(triggerUserInitiatedFeedback());
                            }
                            if (userInfo && !selector.pxApiInitialized) {
                                warningToggle();
                            }
                        }}
                    />
                )}

                {userInfo && jouleEnabled && <ShellBarItem onClick={launchJouleClient} icon="da" data-testid="joule-button" />}
                {userInfo && (
                    <ShellBarItem icon="headset" style={{ border: '1px solid red' }} text="Help" onClick={handleHelp} data-testid="help-icon" />
                )}
            </ShellBar>

            {userInfo && (
                <ShellPopover onClose={toggle} open={isOpen} title={`${userInfo.given_name} ${userInfo.family_name}`} onSignOut={handleLogout} />
            )}

            {isWarningOpen && (
                <MessageBox
                    open={isWarningOpen}
                    type="Warning"
                    onClose={warningToggle}
                    data-testId="feedback-warning"
                    titleText={t('AD_BLOCKER_WARNING_DIALOG_TITLE')}
                    actions={[
                        <Button
                            design="Emphasized"
                            key="okButton"
                            data-testid="okButton"
                            onClick={() => {
                                warningToggle();
                            }}
                        >
                            {t('OK_BUTTON')}
                        </Button>,
                    ]}
                >
                    <Text>{t('AD_BLOCKER_WARNING_DIALOG_DESCRIPTION')}</Text>
                    <ul
                        style={{
                            padding: '10px 0 10px 20px',
                        }}
                    >
                        <li>{t('AD_BLOCKER_WARNING_DIALOG_DISABLE_ADBLOCKER')}</li>
                        <li>{t('AD_BLOCKER_WARNING_DIALOG_REFRESH')}</li>
                    </ul>
                    <Text>{t('AD_BLOCKER_WARNING_DIALOG_CONTACT_ADMIN')}</Text>
                </MessageBox>
            )}
        </>
    );
};

export default UI5ShellBar;
